import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import Helmet from 'react-helmet';

import Spinner from '../../../../components/global/Spinner';
import { APOLLO_NETWORK_STATUS } from '../../../../constants/apollo-network-status';
import WebGalleryHeaderContainer from '../../containers/WebGalleryHeaderContainer';
import {
  AlbumWithVideos,
  AlbumPhotoGroupWithVideos,
} from '../../graphql/queries/album';
import { AlbumFolderWithVideos } from '../../graphql/queries/album-folder';
import { createLoadMoreMethod } from '../../../../util/apollo';
import WebGalleryVideoToolbar from './components/WebGalleryVideoToolbar';
import { getAsset } from '../../../../util/assets';
import ContentWrap from '../../../../components/global/ContentWrap';
import VideoCard from '../../../pro/components/VideoCard';
import VideoGrid from '../../../../components/grid/VideoGrid';
import VideoCardActionButton from '../../../pro/components/VideoCard/components/VideoCardActionButton';
import { VIDEO_DOWNLOAD_LINKS_MODAL } from '../../../../components/grid/components/VideoDownload/modal';
import DownloadIcon from '../../../../components/icons/DownloadIcon';
import useOpenModal from '../../../../hooks/useOpenModal';
import PhotoGridViewerActionButton from '../../../../components/grid/components/PhotoGridViewerActionButton';
import VideoGridViewerFooter from '../../../../components/global/VideoGridViewerFooter';

const propTypes = {
  loading: PropTypes.bool,
  entity: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  fetchingMore: PropTypes.bool,
  fetchMore: PropTypes.func,
  total: PropTypes.number,
};

const WebGalleryVideoListing = ({
  entity,
  loading,
  videos,
  fetchingMore,
  fetchMore,
  total,
}) => {
  const openModal = useOpenModal();

  if (loading) {
    return (
      <div className="d-flex mt-4">
        <Spinner className="m-auto" />
      </div>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Videos &ndash; Waldo</title>
      </Helmet>

      <WebGalleryHeaderContainer />
      <div className="flex-auto content-wrap-gray">
        <WebGalleryVideoToolbar entity={entity} />
        {videos.length > 0 ? (
          <VideoGrid
            loading={fetchingMore}
            records={videos}
            total={total}
            loadMore={fetchMore}
            justifiedLayoutOptions={{
              forceAspectRatio: 1,
            }}
            videoViewerProps={{
              actions: ({ record }) =>
                record.download_enabled && (
                  <PhotoGridViewerActionButton
                    label="Download"
                    icon={<DownloadIcon />}
                    onClick={() =>
                      openModal(VIDEO_DOWNLOAD_LINKS_MODAL, {
                        video: record,
                      })
                    }
                  />
                ),
              footer: ({ record }) => (
                <VideoGridViewerFooter entity={entity} video={record} />
              ),
            }}
            cellRenderer={({ record: video, recordPath }) => {
              return (
                <VideoCard
                  actions={
                    video.download_enabled && (
                      <VideoCardActionButton
                        onClick={() =>
                          openModal(VIDEO_DOWNLOAD_LINKS_MODAL, { video })
                        }
                        icon={<DownloadIcon />}
                      />
                    )
                  }
                  key={video.id}
                  video={video}
                  linkTo={recordPath}
                />
              );
            }}
          />
        ) : (
          <ContentWrap size="sm">
            <div className="text-center">
              <h2 className="mb-3">No videos found</h2>
              <img
                width="100px"
                src={getAsset('/waldo-icon-error.svg')}
                alt="Confused Waldo Icon"
                className="mb-3"
              />

              <p className="text-md">
                It looks like no videos have been added yet. Please come back
                later.
              </p>
            </div>
          </ContentWrap>
        )}
      </div>
    </Fragment>
  );
};

WebGalleryVideoListing.propTypes = propTypes;

const withAlbumFolderWithVideo = graphql(AlbumFolderWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
  }),
  props: ({ data: { albumFolder, networkStatus, fetchMore } }) => ({
    entity: albumFolder,
    videos: albumFolder?.videos.records ?? [],
    total: albumFolder?.videos.total ?? 0,
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: createLoadMoreMethod({
      source: albumFolder,
      recordsPath: 'videos',
      fetchMore,
    }),
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumWithVideo = graphql(AlbumWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album, networkStatus, fetchMore } }) => ({
    entity: album,
    videos: album?.videos.records ?? [],
    total: album?.videos.total ?? 0,
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: createLoadMoreMethod({
      source: album,
      recordsPath: 'videos',
      fetchMore,
    }),
  }),
  skip: ({ match }) => !match.params.albumId || match.params.photoGroupId,
});

const withAlbumPhotoGroupWithVideo = graphql(AlbumPhotoGroupWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.photoGroupId,
    },
  }),
  props: ({ data: { albumPhotoGroup, networkStatus, fetchMore } }) => ({
    entity: albumPhotoGroup,
    videos: albumPhotoGroup?.videos.records ?? [],
    total: albumPhotoGroup?.videos.total ?? 0,
    loading: networkStatus === APOLLO_NETWORK_STATUS.LOADING,
    fetchingMore: networkStatus === APOLLO_NETWORK_STATUS.FETCH_MORE,
    fetchMore: createLoadMoreMethod({
      source: albumPhotoGroup,
      recordsPath: 'videos',
      fetchMore,
    }),
  }),
  skip: ({ match }) => !match.params.photoGroupId,
});

export { WebGalleryVideoListing as WebGalleryVideoListingComponent };

export default compose(
  withRouter,
  withAlbumFolderWithVideo,
  withAlbumWithVideo,
  withAlbumPhotoGroupWithVideo,
)(WebGalleryVideoListing);
